import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from './utils';

export type SwitchProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label?: ReactNode;
  hint?: ReactNode;
};

export const Switch = forwardRef<HTMLInputElement, SwitchProps>(function Switch(
  { label, hint, className, id, ...rest },
  ref,
) {
  const reactId = useId();
  const fieldId = id ?? `switch-${reactId}`;
  return (
    <label
      htmlFor={fieldId}
      className={cn('inline-flex items-start gap-3 cursor-pointer select-none', className)}
    >
      <span className="relative inline-flex items-center w-[46px] h-[27px] rounded-full bg-[rgba(122,106,82,0.30)] shadow-[inset_0_1px_3px_rgba(84,66,36,0.22)] transition-colors mt-0.5 has-[:checked]:bg-[linear-gradient(135deg,#D8B478,#A0793D)] has-[:checked]:shadow-[0_2px_12px_rgba(160,121,61,0.45)]">
        <input
          ref={ref}
          id={fieldId}
          type="checkbox"
          {...rest}
          className="peer absolute inset-0 opacity-0 cursor-pointer"
        />
        <span className="absolute left-[3px] top-1/2 -translate-y-1/2 w-[21px] h-[21px] rounded-full bg-white shadow-card transition-transform duration-300 ease-[cubic-bezier(.32,1.35,.4,1)] peer-checked:translate-x-[19px]" />
      </span>
      {(label || hint) && (
        <span className="leading-tight text-[12.5px] text-ink-700">
          {label && <span className="block font-medium">{label}</span>}
          {hint && <span className="block text-ink-500 text-[11.5px] mt-0.5">{hint}</span>}
        </span>
      )}
    </label>
  );
});
